import { Button, IconButton, Skeleton, styled, useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid2'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { filter, includes, map, toLower } from 'lodash'
import { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AssistantShortDTO, LLMModelDTO } from '../../../../api/gen/base'
import { useInfiniteAssistantListQuery } from '../../../../api/hooks/useInfiniteAssistantListQuery'
import CloseCircleIcon from '../../../../assets/icons/CloseCircle.svg'
import { FreeLabel } from '../../../../components/FreeLabel'
import { useProfileStore } from '../../../../store/storeProfile'
import { useRequestsStore } from '../../../../store/storeRequests'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { theme } from '../../../../theme'
import ShieldCheck from './../../../../assets/icons/ShieldCheck.svg?react'
import { AssistantList } from './AssistantList'
import { ModelIcon } from './ModelIcon'
import { ModelSpecializationIcon } from './ModelSpecializationIcon'
import { SpecializationSelect } from './SpecializationSelect'
import { TabsModelTypes } from './TabsModelTypes'

const EntityTabStyle = styled(Tab)(({ theme }) => ({
    '&': {
        color: '#9195A1',
        borderRadius: 0,
        background: 'none',
    },
    '&.Mui-selected': {
        borderRadius: 0,
        background: '#fff',
        color: '#181B25',
        borderBottom: '2px solid  #004B9F',
    },
}))

const mapTypeModel = ['TEXT', 'IMAGE', 'VIDEO', 'SPEECH', 'AUDIO']

const filterBySpecialization = (items, specializations) => {
    return filter(items, (el) => includes(specializations, el.model_specialization))
}

type Props = {
    open: boolean
    handleClose: VoidFunction
    requestId?: number
    modelList: LLMModelDTO[]
}

export const AssistantSelectionDialog: FC<Props> = ({
    open,
    handleClose,
    requestId,
    modelList,
}) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const [searchParams, setSearchParams] = useSearchParams()
    const { pathname } = useLocation()
    const [type, setType] = useState('0')
    const [entityTab, setEntityTab] = useState('model')
    const [modelListByType, setModelListByType] = useState([])
    const [modelAuto, setModelAutoType] = useState({})
    const navigate = useNavigate()

    const {
        setCurrentAgentAndAssistantIdDialog,
        currentAssistantIdDialog,
        currentAgentNameInDialog,
        connectAssistantId,
        setCurrentAssistantDataInDialog,
        isLoadingConnectAssistantId,
        isProtectedCurrentProject,
        modelListProtected,
        modelDetail,
        isDoneUpdateAssistant,
        setIsDoneUpdateAssistant,
    } = useWrapperStore((state) => state)

    const {
        updateRequest,
        isLoadingUpdateRequest,
        setFilesInRequest,
        files,
        setFile,
        maxFileCountByType,
        setMaxFileCountByType,
        setFilesError,
        availableExtensionsIsRequired,
        setAvailableExtensionsIsRequired,
    } = useRequestsStore((state) => state)
    const { isFullAccess, userTariff } = useProfileStore()

    const { data, fetchNextPage, hasNextPage, isFetching, isFetchedAfterMount, refetch } =
        useInfiniteAssistantListQuery({
            show_restricted: false,
            order_by: 'popularity',
        })

    const assistantList = useMemo(
        () =>
            data?.pages.reduce<AssistantShortDTO[]>((acc, page) => acc.concat(page.objects), []) ??
            [],
        [data],
    )

    const [infiniteRef] = useInfiniteScroll({
        loading: isFetching,
        hasNextPage,
        onLoadMore: fetchNextPage,
    })

    const handleChangeType = (event: SyntheticEvent, newValue: number) => {
        setType(newValue)
    }

    const handleClickAssistantCard = (assistantId) => {
        handleChangeCurrentAgentAndAssistantIdDialog({ agent: 'AUTO', assistantId: assistantId })
    }

    const handleSetEntityTab = (_, entityTab: string) => {
        setEntityTab(entityTab)
    }

    useEffect(() => {
        if (isDoneUpdateAssistant) {
            refetch()
            setIsDoneUpdateAssistant(false)
        }
    }, [isDoneUpdateAssistant, refetch])

    useEffect(() => {
        const modelType = mapTypeModel[type]
        const dataModelList = filter(modelList, { model_type: modelType })

        if (modelType === 'TEXT') {
            const specialization = searchParams.get('specialization')?.split(',')
            const dataModelListBySpecialization = filterBySpecialization(
                dataModelList,
                specialization,
            )
            setModelListByType(dataModelListBySpecialization)
        } else {
            setModelListByType(dataModelList)
        }

        setModelAutoType(filter(modelList, { model_type: 'AUTO' })[0])
    }, [type, modelList, searchParams.get('specialization')])

    const handleChangeCurrentAgentAndAssistantIdDialog = async (value: AssistantShortDTO) => {
        if (
            currentAgentNameInDialog !== value.agent ||
            currentAssistantIdDialog !== value.assistantId
        ) {
            if (requestId) {
                await updateRequest(
                    { threadId: requestId },
                    {
                        assistant_id: value.assistantId,
                        agent: value.agent,
                    },
                )
                await connectAssistantId(
                    { threadId: requestId },
                    { assistant_id: value.assistantId },
                )
            } else {
                setCurrentAgentAndAssistantIdDialog({
                    agent: value.agent,
                    assistantId: value.assistantId,
                })
                if (value.assistantId) {
                    setCurrentAssistantDataInDialog(value)
                }
                if (pathname.split('/')[1] !== 'requests') {
                    if (value.assistantId) {
                        searchParams.set('assistant_id', value.assistantId)
                        setSearchParams(searchParams)
                    } else {
                        searchParams.delete('assistant_id')
                        setSearchParams(searchParams)
                    }
                }
            }
        }

        handleClose()
    }

    const isUpdatingSelection = isLoadingUpdateRequest || isLoadingConnectAssistantId

    return (
        <>
            <Dialog
                slotProps={{
                    paper: {
                        style: {
                            maxWidth: '896px',
                            width: '896px',
                            height: '670px',
                        },
                    },
                }}
                sx={{ overflow: 'hidden' }}
                open={open}
                onClose={handleClose}>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0',
                        padding: '26px 24px',
                    }}>
                    Модели и ассистенты
                    <IconButton
                        sx={{
                            padding: '0',
                        }}
                        onClick={handleClose}>
                        <CloseCircleIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Grid size={6}>
                            <Tabs
                                value={entityTab}
                                onChange={handleSetEntityTab}
                                sx={{
                                    borderBottom: 'none',
                                    background: '#fff',
                                    pt: 0,
                                }}>
                                <EntityTabStyle label="Модели" value="model" />
                                <EntityTabStyle label="Ассистенты" value="assistant" />
                            </Tabs>
                            {entityTab === 'assistant' && (
                                <>
                                    <Grid
                                        sx={{
                                            border: (theme) =>
                                                `2px solid var(--Control-Tinted-TintedEnabled, ${theme.palette.primary.main})`,
                                            padding: '16px',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            gap: '16px',
                                            marginBottom: '8px',
                                            [theme.breakpoints.down('md')]: {
                                                flexDirection: 'column',
                                            },
                                        }}>
                                        <Grid>
                                            <Typography variant="h6">
                                                В разделе Все ассистенты вы найдете помощника под
                                                любую задачу!
                                            </Typography>
                                            <Typography variant="caption2">
                                                Ознакомьтесь с расширенным выбором и попробуйте всех
                                                наших ассистентов
                                            </Typography>
                                        </Grid>
                                        <Button
                                            sx={{
                                                marginLeft: 'auto',
                                                [theme.breakpoints.down('md')]: {
                                                    width: '100%',
                                                },
                                            }}
                                            onClick={() => {
                                                handleClose()
                                                navigate('/assistant-shop')
                                            }}
                                            variant="contained">
                                            Перейти
                                        </Button>
                                    </Grid>
                                    <AssistantList
                                        list={
                                            isProtectedCurrentProject
                                                ? filter(assistantList, {
                                                      is_protected: true,
                                                  })
                                                : assistantList
                                        }
                                        cols={2}
                                        onClickCard={(assistantId: string) =>
                                            handleClickAssistantCard(assistantId)
                                        }
                                        loading={isFetching}
                                        hasNextPage={hasNextPage}
                                    />
                                    {hasNextPage && (
                                        <Skeleton
                                            ref={infiniteRef}
                                            variant="rectangular"
                                            sx={{
                                                borderRadius: '12px',
                                                height: '150px',
                                                width: '100%',
                                                maxHeight: '150px',
                                            }}
                                        />
                                    )}
                                </>
                            )}
                            {entityTab === 'model' && (
                                <>
                                    {modelAuto && (
                                        <RadioGroup
                                            value={
                                                currentAssistantIdDialog
                                                    ? null
                                                    : currentAgentNameInDialog
                                            }>
                                            <Box
                                                sx={{
                                                    my: 3,
                                                    position: 'relative',
                                                    width: '100%',
                                                    border: '1px solid #74B9FF',
                                                    background:
                                                        'linear-gradient( 0deg, rgba(191, 225, 255, 0.4) 0%, rgba(143, 223, 255, 0.4) 55%, rgba(194, 221, 255, 0.4) 100% )',
                                                    borderRadius: '12px',
                                                    padding: '12px 12px 16px 12px',
                                                }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        height: '20px',
                                                        padding: '4px 10px',
                                                        alignItems: 'center',
                                                        gap: '10px',
                                                        background: '#006CFD',
                                                        position: 'absolute',
                                                        left: '20px',
                                                        top: '-20px',
                                                        borderRadius: '8px 8px 0px 0px',
                                                    }}>
                                                    <Typography
                                                        variant="caption2"
                                                        sx={{ color: '#fff' }}>
                                                        Новинка
                                                    </Typography>
                                                </Box>
                                                <FormControlLabel
                                                    disabled={
                                                        !includes(
                                                            userTariff?.tariff?.restrictions
                                                                ?.available_models,
                                                            modelAuto.slug,
                                                        ) || isUpdatingSelection
                                                    }
                                                    onChange={() =>
                                                        handleChangeCurrentAgentAndAssistantIdDialog(
                                                            {
                                                                agent: modelAuto.slug,
                                                                assistantId: null,
                                                            },
                                                        )
                                                    }
                                                    sx={{
                                                        m: 0,
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        '&.Mui-disabled': {
                                                            opacity: '0.5',
                                                        },
                                                    }}
                                                    labelPlacement="start"
                                                    value={modelAuto.slug}
                                                    control={<Radio />}
                                                    label={
                                                        <>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    gap: '4px',
                                                                }}>
                                                                <ModelIcon
                                                                    agentKey={modelAuto.slug}
                                                                />
                                                                <Typography
                                                                    variant="caption1"
                                                                    component="p"
                                                                    sx={{
                                                                        color: '#181B25',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                    }}>
                                                                    {modelAuto.title}
                                                                </Typography>
                                                            </Box>
                                                        </>
                                                    }
                                                />
                                                <Typography
                                                    variant="caption2"
                                                    component="p"
                                                    sx={{
                                                        color: '#5E626E',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                    }}>
                                                    {modelAuto.description}
                                                </Typography>
                                            </Box>
                                        </RadioGroup>
                                    )}

                                    <Stack
                                        mb={2}
                                        direction="row"
                                        sx={{
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}>
                                        <TabsModelTypes
                                            value={type}
                                            onChange={handleChangeType}
                                            modelList={
                                                isProtectedCurrentProject
                                                    ? modelListProtected
                                                    : modelList
                                            }
                                        />
                                        {!isMobile && mapTypeModel[type] === 'TEXT' && (
                                            <SpecializationSelect
                                                isProtectedCurrentProject={
                                                    isProtectedCurrentProject
                                                }
                                            />
                                        )}
                                    </Stack>
                                    {isMobile && mapTypeModel[type] === 'TEXT' && (
                                        <SpecializationSelect
                                            isProtectedCurrentProject={isProtectedCurrentProject}
                                        />
                                    )}
                                    <FormControl sx={{ marginY: '12px', width: '100%' }}>
                                        <RadioGroup
                                            value={currentAgentNameInDialog}
                                            sx={{
                                                justifyContent: 'space-between',
                                                flexDirection: 'row',
                                            }}>
                                            {modelListByType.length > 0 && (
                                                <Grid
                                                    container
                                                    spacing={isMobile ? 1 : 2}
                                                    sx={{
                                                        marginLeft: '8px',
                                                        marginBottom: '8px',
                                                        borderRadius: '12px',
                                                        background: '#fff',
                                                    }}>
                                                    {map(
                                                        modelListByType,
                                                        ({
                                                            title,
                                                            id,
                                                            slug,
                                                            description,
                                                            model_type,
                                                            model_specialization,
                                                            is_free,
                                                            is_protected,
                                                        }) => {
                                                            return (
                                                                <Grid
                                                                    size={isMobile ? 12 : 6}
                                                                    key={`model_${id}`}
                                                                    sx={{
                                                                        border: '1px solid #F4F4F6',
                                                                        marginBottom: '8px',
                                                                        background: '#fff',
                                                                        borderRadius: '12px',
                                                                        padding:
                                                                            '12px 12px 16px 12px',
                                                                    }}>
                                                                    <FormControlLabel
                                                                        onChange={() =>
                                                                            handleChangeCurrentAgentAndAssistantIdDialog(
                                                                                {
                                                                                    agent: slug,
                                                                                    assistantId:
                                                                                        null,
                                                                                },
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            m: 0,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            '&  span.MuiTypography-root':
                                                                                {
                                                                                    width: '100%',
                                                                                },
                                                                            '&.Mui-disabled': {
                                                                                opacity: '0.5',
                                                                            },
                                                                        }}
                                                                        labelPlacement="start"
                                                                        value={slug}
                                                                        disabled={
                                                                            isUpdatingSelection ||
                                                                            !includes(
                                                                                userTariff?.tariff
                                                                                    ?.restrictions
                                                                                    ?.available_models,
                                                                                slug,
                                                                            )
                                                                        }
                                                                        control={<Radio />}
                                                                        label={
                                                                            <>
                                                                                <Stack
                                                                                    direction="row"
                                                                                    sx={{
                                                                                        alignItems:
                                                                                            'center',
                                                                                        gap: '4px',
                                                                                        justifyContent:
                                                                                            is_free
                                                                                                ? 'space-between'
                                                                                                : 'flex-start',
                                                                                    }}>
                                                                                    <Stack
                                                                                        sx={{
                                                                                            alignItems:
                                                                                                'center',
                                                                                        }}
                                                                                        direction="row"
                                                                                        spacing={1}>
                                                                                        <ModelIcon
                                                                                            agentKey={
                                                                                                slug
                                                                                            }
                                                                                        />
                                                                                        <Typography
                                                                                            id={`model_${toLower(model_type)}_names`}
                                                                                            variant="caption1"
                                                                                            component="p"
                                                                                            sx={{
                                                                                                display:
                                                                                                    'flex',
                                                                                                alignItems:
                                                                                                    'center',
                                                                                            }}>
                                                                                            {title}
                                                                                        </Typography>

                                                                                        {model_specialization && (
                                                                                            <Stack
                                                                                                sx={{
                                                                                                    background:
                                                                                                        '#F4F4F6',
                                                                                                    borderRadius:
                                                                                                        '6px',
                                                                                                    '&:hover':
                                                                                                        {
                                                                                                            borderRadius:
                                                                                                                '6px',
                                                                                                        },
                                                                                                    width: '20px',
                                                                                                    height: '20px',
                                                                                                    justifyContent:
                                                                                                        'center',
                                                                                                    alignItems:
                                                                                                        'center',
                                                                                                }}>
                                                                                                <ModelSpecializationIcon
                                                                                                    style={{
                                                                                                        width: '15px',
                                                                                                        height: '15px',
                                                                                                    }}
                                                                                                    specialization={
                                                                                                        model_specialization
                                                                                                    }
                                                                                                />
                                                                                            </Stack>
                                                                                        )}
                                                                                        {is_protected && (
                                                                                            <Tooltip
                                                                                                enterTouchDelay={
                                                                                                    0
                                                                                                }
                                                                                                title="Защищенная нейросеть">
                                                                                                <IconButton
                                                                                                    sx={{
                                                                                                        p: 0,
                                                                                                        width: '20px',
                                                                                                        height: '20px',
                                                                                                    }}>
                                                                                                    <ShieldCheck
                                                                                                        style={{
                                                                                                            color: '#5A5C5E',
                                                                                                            height: '16px',
                                                                                                            width: '16px',
                                                                                                        }}
                                                                                                    />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        )}
                                                                                    </Stack>
                                                                                    {is_free && (
                                                                                        <FreeLabel />
                                                                                    )}
                                                                                </Stack>
                                                                            </>
                                                                        }
                                                                    />
                                                                    <Typography
                                                                        variant="caption2"
                                                                        component="p"
                                                                        sx={{
                                                                            color: '#5E626E',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                        }}>
                                                                        {description}
                                                                    </Typography>
                                                                </Grid>
                                                            )
                                                        },
                                                    )}
                                                </Grid>
                                            )}
                                        </RadioGroup>
                                    </FormControl>
                                    {modelListByType.length === 0 && (
                                        <Box
                                            sx={{
                                                marginLeft: 1,
                                                marginBottom: 1,
                                                with: '100%',
                                                borderRadius: '12px',
                                                background:
                                                    'linear-gradient( 0deg, rgba(191, 225, 255, 0.4) 0%, rgba(143, 223, 255, 0.4) 55%, rgba(194, 221, 255, 0.4) 100% )',
                                                padding: '24px',
                                                border: '1px solid #74B9FF',
                                            }}>
                                            <Typography
                                                variant="caption1"
                                                sx={{ textAlign: 'center' }}>
                                                Обнови тариф и получи доступ к моделям
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'end' }} />
            </Dialog>
        </>
    )
}
